import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(() => {
    const t = localStorage.getItem("token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const t = localStorage.getItem("token");
      const headers = t ? { Authorization: `Bearer ${t}` } : {};
      const { data } = await axios.get(`${API}/auth/me`, { headers });
      setUser(data);
    } catch {
      localStorage.removeItem("token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth (URL fragment has session_id), skip /me check.
    // AuthCallback will exchange the session_id first, then reload user.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await axios.post(`${API}/auth/register`, payload);
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { headers: authHeaders() }); }
    catch (e) { console.warn("Logout request failed:", e?.message || e); }
    localStorage.removeItem("token");
    setUser(null);
  }, [authHeaders]);

  const value = useMemo(() => ({
    user, loading, login, register, logout, refresh: fetchMe, authHeaders, setUser,
  }), [user, loading, login, register, logout, fetchMe, authHeaders]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function formatApiError(detail) {
  if (detail == null) return "Xəta baş verdi";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
